import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Container,
  Stack,
  IconButton,
  Alert,
  Chip,
  alpha,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  Download,
  CloudUpload,
  CheckCircle,
  Description,
  PictureAsPdf,
  Add,
  Delete,
  Groups,
  CameraAlt
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import FileUploadComponent from '../common/FileUploadComponent';

/**
 * Section for Candidate to Download templates, fill them, and upload.
 */
const JoiningFormsSection = ({
  joiningFormsDraft = {},
  onJoiningFormsChange,
  onFileUpload,
  onFileRemove,
  validationErrors = {},
  onClearError,
  uploadedForms = [],
  requirements = [], // Dynamic requirements from backend
  personalDraft = {},
  employeeData = {},
  isSubmitting,
  rejectedDocs
}) => {
  const theme = useTheme();

  const getRejectionError = (docType) => {
    if (!rejectedDocs) return null;
    const doc = rejectedDocs.find(d =>
      d.documentType === docType ||
      d.documentType === `Joining Form: ${docType}` ||
      d.documentType === `Experience: ${docType}` ||
      d.documentType.startsWith(`Experience: ${docType}`) ||
      d.documentType.replace(/^Joining Form:\s*/i, '').replace(/^Experience:\s*/i, '').trim().toLowerCase() === docType.toLowerCase()
    );
    return doc ? `Rejected: ${doc.rejectionReason}` : null;
  };


  const collect12BB = employeeData?.compensation?.collect12BB;
  const has12BB = collect12BB === true || collect12BB === 'true' || collect12BB === 'yes';

  const forms = useMemo(() => {
    const hasExistingPF = employeeData?.hasExistingPF ?? employeeData?.compensation?.hasExistingPF;
    const hasPF = hasExistingPF === true || hasExistingPF === 'true' || hasExistingPF === 'yes';

    const list = requirements
      .filter(req => {
        const formName = req.template?.templateName || req.templateName || req.label;
        if (!hasPF && (formName === 'form11' || formName === 'pfForm2')) {
          return false;
        }
        return true;
      })
      .map(req => ({
        id: req.id,
        name: req.template?.templateName || req.template?.label || "Joining Form",
        label: req.template?.label || req.template?.templateName || "Joining Form",
        fileUrl: req.template?.fileUrl,
        description: req.template?.description || `Please download and upload the signed ${req.template?.label || req.template?.templateName || 'form'}.`,
        required: req.isMandatory,
        isNomineeRequired: req.isNomineeRequired || req.template?.isNomineeRequired
      }));

    // Add Passport Size Photo checklist item
    list.push({
      id: 'passport-photo',
      name: 'passportPhoto',
      label: 'Passport Size Photo',
      fileUrl: null,
      description: 'Please upload a high-quality, professional passport-size photograph.',
      required: true,
      accept: "image/jpeg,image/jpg,image/png"
    });

    list.push({
      id: 'signed-offer-letter',
      name: 'Signed Offer Letter',
      label: 'Signed Offer Letter',
      fileUrl: null,
      description: 'Please upload the signed Offer Letter from the company.',
      required: true,
      accept: ".pdf,.jpg,.jpeg,.png"
    });

    if (has12BB) {
      list.push({
        id: 'tax-declaration-12bb',
        name: 'Form 12BB',
        label: 'Form 12BB (Tax Declaration)',
        fileUrl: '/12BB Form.pdf',
        description: 'Please download, fill out, and upload your Form 12BB for Tax Declaration.',
        required: true
      });
    }

    const collectESIC = employeeData?.compensation?.collectESIC;
    const hasESIC = collectESIC === true || collectESIC === 'true' || collectESIC === 'yes';
    if (hasESIC) {
      list.push({
        id: 'esic-form',
        name: 'ESIC Form',
        label: 'ESIC Form',
        fileUrl: '/ESIC Form.pdf',
        description: 'Please download, fill out, and upload your ESIC Form.',
        required: true,
        isNomineeRequired: true
      });
    }

    if (hasPF) {
      list.push({
        id: 'declaration-non-pf',
        name: 'Declaration of Non-PF',
        label: 'Declaration of Non-PF',
        fileUrl: '/Declaration Of Non-PF.pdf',
        description: 'Please download, fill out, and upload your Declaration of Non-PF.',
        required: true
      });
    }

    return list;
  }, [requirements, employeeData, has12BB]);

  const handleDownload = (url, fileName) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.target = "_blank";
    link.download = fileName || 'template';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUploadWithClearError = (files, section, index, formName) => {
    if (validationErrors[formName] && onClearError) {
      onClearError(formName);
    }
    onFileUpload(files, section, index, formName);
  };



  const needsNominees = useMemo(() => {
    return forms.some(f => f.isNomineeRequired);
  }, [forms]);

  const nominees = useMemo(() => {
    return joiningFormsDraft.nominees || [];
  }, [joiningFormsDraft.nominees]);

  React.useEffect(() => {
    if (needsNominees && (!joiningFormsDraft.nominees || joiningFormsDraft.nominees.length === 0)) {
      onJoiningFormsChange('nominees', [
        {
          nomineeName: "",
          contactNo: "",
          nomineeDob: "",
          relation: "",
          aadhaarNumber: "",
          aadhaarFrontPath: "",
          aadhaarBackPath: "",
          nomineeType: "Dependent",
          photoPath: ""
        }
      ]);
    }
  }, [needsNominees, joiningFormsDraft.nominees, onJoiningFormsChange]);

  const handleNomineeChange = (idx, field, value) => {
    const newNominees = [...nominees];
    newNominees[idx] = { ...newNominees[idx], [field]: value };
    onJoiningFormsChange('nominees', newNominees);
    if (onClearError) {
      onClearError(`nomineeName_${idx}`);
      onClearError(`nomineeRelation_${idx}`);
      onClearError(`nomineeContact_${idx}`);
      onClearError(`nomineeDob_${idx}`);
      onClearError(`nomineeAadhaarNumber_${idx}`);
      onClearError(`nomineeAadhaarFront_${idx}`);
      onClearError(`nomineeAadhaarBack_${idx}`);
    }
  };

  const addNominee = () => {
    const updatedNominees = [...nominees];
    updatedNominees.push({
      nomineeName: "",
      contactNo: "",
      nomineeDob: "",
      relation: "",
      aadhaarNumber: "",
      aadhaarFrontPath: "",
      aadhaarBackPath: "",
      nomineeType: "Dependent",
      photoPath: ""
    });
    onJoiningFormsChange('nominees', updatedNominees);
  };

  const removeNominee = (idx) => {
    const filteredNominees = nominees.filter((_, i) => i !== idx);
    onJoiningFormsChange('nominees', filteredNominees);
  };

  const isUploaded = (formName) => {
    return uploadedForms.includes(formName) && !getRejectionError(formName);
  };







  const inputGlobalStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      bgcolor: '#F8FAFC',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: '#E2E8F0',
        transition: 'all 0.2s ease',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-disabled': {
        bgcolor: alpha(theme.palette.action.disabledBackground, 0.03),
      },
      '&.Mui-disabled fieldset': {
        borderColor: '#F1F5F9',
      },
      '&.Mui-error fieldset': {
        borderColor: theme.palette.error.main,
      },
      '&.Mui-error:hover fieldset': {
        borderColor: theme.palette.error.dark,
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiSelect-select': {
      borderRadius: '12px',
    }
  };

  return (
    <Container maxWidth="xl" sx={{ pb: 6, position: 'relative' }}>
      {isSubmitting && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1000,
            borderRadius: '16px',
            backdropFilter: 'blur(4px)',
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
        </Box>
      )}
      <Box sx={inputGlobalStyles}>
        {/* Hero Header Section */}
        <Card
          elevation={0}
          sx={{
            mb: 5,
            background: 'linear-gradient(135deg, #7522D1 0%, #4D1293 100%)',
            color: '#FFFFFF',
            borderRadius: 5,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 10px 30px -10px rgba(117, 34, 209, 0.4)',
          }}
        >
          {/* Decorative background shapes */}
          <Box
            sx={{
              position: 'absolute',
              top: -40,
              right: -40,
              width: 180,
              height: 180,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.08)',
              filter: 'blur(20px)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -60,
              left: '20%',
              width: 250,
              height: 250,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.05)',
              filter: 'blur(30px)',
              pointerEvents: 'none',
            }}
          />

          <CardContent sx={{ p: { xs: 4, md: 5 }, position: 'relative', zIndex: 1 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: '#FFFFFF',
                    mb: 1.5,
                    letterSpacing: '-0.02em',
                    fontSize: { xs: '1.75rem', md: '2.25rem' }
                  }}
                >
                  Joining Forms Checklist
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontWeight: 400,
                    maxWidth: '600px',
                    lineHeight: 1.6
                  }}
                >
                  Please complete your onboarding by downloading the template files, filling/signing them, and uploading them below. Your progress keeps you on track for a smooth start!
                </Typography>
              </Grid>
              <Grid
                size={{ xs: 12, md: 4 }}
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  alignItems: 'center'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    px: 3,
                    py: 2,
                    borderRadius: 4,
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                  }}
                >
                  <Description sx={{ fontSize: 32, color: '#E9D5FF' }} />
                  <Box>
                    <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 800, lineHeight: 1 }}>
                      {forms.length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 }}>
                      Required Tasks
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={4} sx={{ mb: 5 }}>
          {/* Onboarding Preferences Card (Left: md 12) */}
          <Grid size={{ xs: 12, md: 12 }}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 5,
                border: '1px solid #E2E8F0',
                overflow: 'hidden',
                boxShadow: '0 4px 20px -2px rgba(117, 34, 209, 0.03)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box
                sx={{
                  p: 3,
                  bgcolor: '#FBF9FF',
                  borderBottom: '1px solid #F1F5F9',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: alpha('#7522D1', 0.1),
                    color: '#7522D1',
                    display: 'flex'
                  }}
                >
                  <CheckCircle sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={800} color="#1E293B">
                  Onboarding Preferences
                </Typography>
              </Box>
              <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}>
                {/* Expected Joining Date Field */}
                <Box>
                  <Grid container spacing={3} alignItems="center">
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" fontWeight={700} color="#334155" gutterBottom>
                        Expected Date of Joining *
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Please select the date you intend to officially join our organization.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        type="date"
                        value={joiningFormsDraft.expectedJoiningDate || ''}
                        onChange={(e) => onJoiningFormsChange('expectedJoiningDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                        error={!!validationErrors.expectedJoiningDate}
                        helperText={validationErrors.expectedJoiningDate || "Official joining date"}
                        inputProps={{
                          min: new Date().toISOString().split('T')[0]
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: '#F8FAFC',
                            '& fieldset': { borderColor: '#E2E8F0' },
                            '&:hover fieldset': { borderColor: '#7522D1' },
                            '&.Mui-focused fieldset': { borderColor: '#7522D1' }
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Tax Declaration Selection */}
              </CardContent>
            </Card>
          </Grid>


        </Grid>

        {/* Forms Section Header */}
        <Box sx={{ mt: 6, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1E293B', letterSpacing: '-0.01em', mb: 1 }}>
            Required Documents Checklist
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Please download templates if needed, fill/sign them, and upload the signed copies for verification.
          </Typography>
        </Box>

        {/* Forms Grid */}
        <Grid container spacing={4} justifyContent="center">
          {forms.map((form) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }} key={form.name}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 5,
                  borderLeft: `5px solid ${isUploaded(form.name) ? '#10B981' : (getRejectionError(form.name) ? theme.palette.error.main : '#E2E8F0')}`,
                  height: '100%',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  bgcolor: getRejectionError(form.name) ? alpha(theme.palette.error.main, 0.03) : '#FFFFFF',
                  position: 'relative',
                  overflow: 'hidden',
                  border: getRejectionError(form.name) ? `1px solid ${theme.palette.error.main}` : '1px solid #E2E8F0',
                  '&:hover': {
                    boxShadow: '0 12px 24px -10px rgba(117, 34, 209, 0.15)',
                    transform: 'translateY(-6px)',
                    borderLeftColor: isUploaded(form.name) ? '#10B981' : (getRejectionError(form.name) ? theme.palette.error.main : '#7522D1'),
                    borderColor: getRejectionError(form.name) ? theme.palette.error.main : '#E2E8F0',
                  }
                }}
              >
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
                    <Box sx={{
                      p: 1.5,
                      borderRadius: 3,
                      bgcolor: isUploaded(form.name) ? alpha('#10B981', 0.1) : alpha('#7522D1', 0.1),
                      color: isUploaded(form.name) ? '#10B981' : '#7522D1',
                      display: 'flex'
                    }}>
                      {isUploaded(form.name) ? (
                        <CheckCircle />
                      ) : (
                        form.name === 'passportPhoto' ? <CameraAlt /> : <PictureAsPdf />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2, mb: 0.5, color: getRejectionError(form.name) ? 'error.main' : '#1E293B' }}>
                        {form.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 1 }}>
                        {form.description}
                      </Typography>
                      {isUploaded(form.name) && (
                        <Chip
                          label="VERIFIED & UPLOADED"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 800,
                            borderRadius: 1,
                            bgcolor: alpha('#10B981', 0.1),
                            color: '#059669',
                            border: '1px solid rgba(16, 185, 129, 0.2)'
                          }}
                        />
                      )}
                    </Box>
                  </Stack>

                  <Box sx={{ mt: 'auto', pt: 2 }}>
                    <Stack spacing={1.5}>
                      {form.fileUrl && (
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleDownload(form.fileUrl, form.label)}
                          fullWidth
                          disabled={isUploaded(form.name)}
                          sx={{
                            borderRadius: 3,
                            textTransform: 'none',
                            justifyContent: 'flex-start',
                            color: '#7522D1',
                            fontWeight: 600,
                            py: 1,
                            px: 2,
                            bgcolor: alpha('#7522D1', 0.04),
                            '&:hover': { bgcolor: alpha('#7522D1', 0.08), color: '#7522D1' }
                          }}
                        >
                          Download Template
                        </Button>
                      )}

                      <FileUploadComponent
                        label={isUploaded(form.name) ? "View Documents" : (form.name === 'passportPhoto' ? "Choose Photograph to Upload" : "Choose File(s) to Upload")}
                        files={joiningFormsDraft[form.name] || []}
                        onFileUpload={(files) => !isUploaded(form.name) && handleFileUploadWithClearError(files, 'joiningForms', null, form.name)}
                        onFileRemove={(index) => !isUploaded(form.name) && onFileRemove('joiningForms', null, index, form.name)}
                        disabled={isUploaded(form.name)}
                        multiple={form.name === 'passportPhoto' ? false : true}
                        showChips={true}
                        required={form.required && !isUploaded(form.name)}
                        error={!!validationErrors[form.name] || !!getRejectionError(form.name)}
                        accept={form.accept || ".pdf,.jpg,.jpeg,.png"}
                        helperText={getRejectionError(form.name) || validationErrors[form.name] || (isUploaded(form.name) ? "Document verified and locked" : (form.name === 'passportPhoto' ? "Upload a high-quality, professional passport-size photo (JPG/PNG)" : "Upload one or more signed pages (JPG/PDF)"))}
                      />
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {needsNominees && (
          <Box sx={{ mt: 6 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 5,
                border: '1px solid #E2E8F0',
                overflow: 'hidden',
                boxShadow: '0 4px 20px -2px rgba(117, 34, 209, 0.03)'
              }}
            >
              <Box
                sx={{
                  p: 3,
                  bgcolor: '#FBF9FF',
                  borderBottom: `1px solid #F1F5F9`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: alpha('#7522D1', 0.1),
                      color: '#7522D1',
                      display: 'flex'
                    }}
                  >
                    <Groups sx={{ fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={800} color="#1E293B" sx={{ lineHeight: 1.2 }}>
                      Dependent Details
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Please provide dependent information.
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<Add />}
                  onClick={addNominee}
                  sx={{
                    borderRadius: 3,
                    textTransform: 'none',
                    bgcolor: '#7522D1',
                    boxShadow: '0 4px 12px rgba(117, 34, 209, 0.25)',
                    px: 3,
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: '#5a1aa8',
                      boxShadow: '0 6px 16px rgba(117, 34, 209, 0.35)',
                    }
                  }}
                >
                  Add Dependent
                </Button>
              </Box>
              <CardContent sx={{ p: { xs: 3, md: 4 }, bgcolor: '#FAF8FF' }}>
                {validationErrors.nominees && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 4,
                      borderRadius: 3,
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      fontWeight: 500
                    }}
                  >
                    {validationErrors.nominees}
                  </Alert>
                )}

                {nominees.map((nominee, idx) => (
                  <Card
                    key={nominee.id || idx}
                    elevation={0}
                    sx={{
                      p: { xs: 3, md: 4 },
                      mb: idx === nominees.length - 1 ? 0 : 4,
                      borderRadius: 4,
                      border: '1px solid #E2E8F0',
                      bgcolor: '#FFFFFF',
                      position: 'relative',
                      transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)',
                        borderColor: '#7522D1'
                      }
                    }}
                  >
                    {nominees.length > 1 && (
                      <IconButton
                        color="error"
                        onClick={() => removeNominee(idx)}
                        sx={{
                          position: 'absolute',
                          top: 20,
                          right: 20,
                          bgcolor: '#FFF5F5',
                          border: '1px solid #FED7D7',
                          color: '#E53E3E',
                          '&:hover': {
                            bgcolor: '#FED7D7',
                            transform: 'scale(1.05)'
                          },
                          transition: 'all 0.2s ease',
                          width: 36,
                          height: 36
                        }}
                        size="small"
                      >
                        <Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}

                    {/* Header info with nominee avatar */}
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha('#7522D1', 0.1),
                          color: '#7522D1',
                          fontWeight: 800,
                          fontSize: '0.9rem',
                          width: 36,
                          height: 36,
                          border: '2px solid #FFFFFF',
                          boxShadow: '0 2px 8px rgba(117,34,209,0.15)'
                        }}
                      >
                        {idx + 1}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={800} color="#1E293B" sx={{ lineHeight: 1 }}>
                          Dependent Details #{idx + 1}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {nominee.relation ? `${nominee.relation} Relation` : 'Specify relationship below'}
                        </Typography>
                      </Box>
                    </Stack>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                          label="Nominee Name (As per Aadhaar)"
                          size="small"
                          fullWidth
                          value={nominee.nomineeName || ''}
                          onChange={(e) => handleNomineeChange(idx, 'nomineeName', e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50))}
                          error={!!validationErrors[`nomineeName_${idx}`]}
                          helperText={validationErrors[`nomineeName_${idx}`] || "Enter nominee's full name"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 3,
                              bgcolor: '#F8FAFC',
                              '& fieldset': { borderColor: '#E2E8F0' },
                              '&:hover fieldset': { borderColor: '#7522D1' },
                              '&.Mui-focused fieldset': { borderColor: '#7522D1' }
                            }
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                          label="Contact Number"
                          size="small"
                          fullWidth
                          value={nominee.contactNo || ''}
                          onChange={(e) => handleNomineeChange(idx, 'contactNo', e.target.value.replace(/\D/g, '').slice(0, 10))}
                          error={!!validationErrors[`nomineeContact_${idx}`]}
                          helperText={validationErrors[`nomineeContact_${idx}`] || "Enter 10-digit mobile number"}
                          inputProps={{ maxLength: 10 }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 3,
                              bgcolor: '#F8FAFC',
                              '& fieldset': { borderColor: '#E2E8F0' },
                              '&:hover fieldset': { borderColor: '#7522D1' },
                              '&.Mui-focused fieldset': { borderColor: '#7522D1' }
                            }
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                          label="Date of Birth"
                          size="small"
                          type="date"
                          fullWidth
                          value={nominee.nomineeDob || ''}
                          onChange={(e) => handleNomineeChange(idx, 'nomineeDob', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          error={!!validationErrors[`nomineeDob_${idx}`]}
                          helperText={validationErrors[`nomineeDob_${idx}`] || "Select date of birth"}
                          inputProps={{ max: new Date().toISOString().split('T')[0] }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 3,
                              bgcolor: '#F8FAFC',
                              '& fieldset': { borderColor: '#E2E8F0' },
                              '&:hover fieldset': { borderColor: '#7522D1' },
                              '&.Mui-focused fieldset': { borderColor: '#7522D1' }
                            }
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel id={`nominee-relation-label-${idx}`} sx={{ '&.Mui-focused': { color: '#7522D1' } }}>Relation</InputLabel>
                          <Select
                            labelId={`nominee-relation-label-${idx}`}
                            value={nominee.relation || ''}
                            label="Relation"
                            onChange={(e) => handleNomineeChange(idx, 'relation', e.target.value)}
                            error={!!validationErrors[`nomineeRelation_${idx}`]}
                            sx={{
                              borderRadius: 3,
                              bgcolor: '#F8FAFC',
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7522D1' },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7522D1' }
                            }}
                          >
                            <MenuItem value="Father" disabled={nominees.some((n, i) => i !== idx && n.relation === 'Father')}>Father</MenuItem>
                            <MenuItem value="Mother" disabled={nominees.some((n, i) => i !== idx && n.relation === 'Mother')}>Mother</MenuItem>
                            <MenuItem value="Spouse" disabled={nominees.some((n, i) => i !== idx && n.relation === 'Spouse')}>Spouse</MenuItem>
                            <MenuItem value="Sibling">Sibling</MenuItem>
                            <MenuItem value="Child">Child</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                          label="Dependent Aadhaar Number"
                          size="small"
                          fullWidth
                          value={nominee.aadhaarNumber || ''}
                          onChange={(e) => handleNomineeChange(idx, 'aadhaarNumber', e.target.value.replace(/\D/g, '').slice(0, 12))}
                          error={!!validationErrors[`nomineeAadhaarNumber_${idx}`]}
                          helperText={validationErrors[`nomineeAadhaarNumber_${idx}`] || "Enter 12-digit Aadhaar number"}
                          inputProps={{ maxLength: 12 }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 3,
                              bgcolor: '#F8FAFC',
                              '& fieldset': { borderColor: '#E2E8F0' },
                              '&:hover fieldset': { borderColor: '#7522D1' },
                              '&.Mui-focused fieldset': { borderColor: '#7522D1' }
                            }
                          }}
                        />
                      </Grid>

                      {/* Empty spacing grid column to align the Aadhaar copies to next row nicely */}
                      <Grid size={{ xs: 0, md: 4 }} />

                      {/* Aadhaar Front/Back Copy Upload boxes */}
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          sx={{
                            p: 2.5,
                            borderRadius: 3,
                            border: '1px dashed #CBD5E1',
                            bgcolor: '#F8FAFC',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#7522D1',
                              bgcolor: alpha('#7522D1', 0.01)
                            }
                          }}
                        >
                          <Typography variant="caption" fontWeight={700} color="#475569" display="block" sx={{ mb: 1.5 }}>
                            Aadhaar Card Front Copy *
                          </Typography>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                              variant="outlined"
                              component="label"
                              size="small"
                              startIcon={<CloudUpload />}
                              sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                borderColor: '#7522D1',
                                color: '#7522D1',
                                fontWeight: 600,
                                px: 2,
                                '&:hover': {
                                  bgcolor: alpha('#7522D1', 0.05),
                                  borderColor: '#7522D1'
                                }
                              }}
                            >
                              Upload Front (Max 4MB)
                              <input
                                type="file"
                                hidden
                                accept="image/*,.pdf"
                                onChange={(e) => {
                                  if (e.target.files && e.target.files[0]) {
                                    handleNomineeChange(idx, 'aadhaarFrontFile', e.target.files[0]);
                                  }
                                }}
                              />
                            </Button>
                            {nominee.aadhaarFrontFile && (
                              <Typography variant="caption" noWrap sx={{ maxWidth: 180, fontWeight: 600 }} color="success.main">
                                {nominee.aadhaarFrontFile.name}
                              </Typography>
                            )}
                            {!nominee.aadhaarFrontFile && nominee.aadhaarFrontPath && (
                              <Typography variant="caption" noWrap sx={{ maxWidth: 180, fontWeight: 600 }} color="primary.main">
                                Uploaded & Secure
                              </Typography>
                            )}
                          </Stack>
                          {validationErrors[`nomineeAadhaarFront_${idx}`] && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
                              {validationErrors[`nomineeAadhaarFront_${idx}`]}
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          sx={{
                            p: 2.5,
                            borderRadius: 3,
                            border: '1px dashed #CBD5E1',
                            bgcolor: '#F8FAFC',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#7522D1',
                              bgcolor: alpha('#7522D1', 0.01)
                            }
                          }}
                        >
                          <Typography variant="caption" fontWeight={700} color="#475569" display="block" sx={{ mb: 1.5 }}>
                            Aadhaar Card Back Copy *
                          </Typography>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                              variant="outlined"
                              component="label"
                              size="small"
                              startIcon={<CloudUpload />}
                              sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                borderColor: '#7522D1',
                                color: '#7522D1',
                                fontWeight: 600,
                                px: 2,
                                '&:hover': {
                                  bgcolor: alpha('#7522D1', 0.05),
                                  borderColor: '#7522D1'
                                }
                              }}
                            >
                              Upload Back (Max 4MB)
                              <input
                                type="file"
                                hidden
                                accept="image/*,.pdf"
                                onChange={(e) => {
                                  if (e.target.files && e.target.files[0]) {
                                    handleNomineeChange(idx, 'aadhaarBackFile', e.target.files[0]);
                                  }
                                }}
                              />
                            </Button>
                            {nominee.aadhaarBackFile && (
                              <Typography variant="caption" noWrap sx={{ maxWidth: 180, fontWeight: 600 }} color="success.main">
                                {nominee.aadhaarBackFile.name}
                              </Typography>
                            )}
                            {!nominee.aadhaarBackFile && nominee.aadhaarBackPath && (
                              <Typography variant="caption" noWrap sx={{ maxWidth: 180, fontWeight: 600 }} color="primary.main">
                                Uploaded & Secure
                              </Typography>
                            )}
                          </Stack>
                          {validationErrors[`nomineeAadhaarBack_${idx}`] && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
                              {validationErrors[`nomineeAadhaarBack_${idx}`]}
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      {/* Dependent Photo Upload box */}
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          sx={{
                            p: 2.5,
                            borderRadius: 3,
                            border: '1px dashed #CBD5E1',
                            bgcolor: '#F8FAFC',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#7522D1',
                              bgcolor: alpha('#7522D1', 0.01)
                            }
                          }}
                        >
                          <Typography variant="caption" fontWeight={700} color="#475569" display="block" sx={{ mb: 1.5 }}>
                            Dependent Photo Upload (Optional)
                          </Typography>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                              variant="outlined"
                              component="label"
                              size="small"
                              startIcon={<CloudUpload />}
                              sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                borderColor: '#7522D1',
                                color: '#7522D1',
                                fontWeight: 600,
                                px: 2,
                                '&:hover': {
                                  bgcolor: alpha('#7522D1', 0.05),
                                  borderColor: '#7522D1'
                                }
                              }}
                            >
                              Upload Photo
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => {
                                  if (e.target.files && e.target.files[0]) {
                                    handleNomineeChange(idx, 'photoFile', e.target.files[0]);
                                  }
                                }}
                              />
                            </Button>
                            {nominee.photoFile && (
                              <Typography variant="caption" noWrap sx={{ maxWidth: 180, fontWeight: 600 }} color="success.main">
                                {nominee.photoFile.name}
                              </Typography>
                            )}
                            {!nominee.photoFile && nominee.photoPath && (
                              <Typography variant="caption" noWrap sx={{ maxWidth: 180, fontWeight: 600 }} color="primary.main">
                                Uploaded & Secure
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                ))}
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default JoiningFormsSection;
